<script setup>
// «Тикеты» tab (TOPS-213): the tickets the operator is on — made, has, or
// was added to — across chats. A ticket opens on its own; the chat opens
// only from its button, and only for those who have the chat.
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import OperatorTicketsAPI from 'dashboard/api/operatorTickets';
import OperatorTicketDetail from '../../conversation/OperatorTicketDetail.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const store = useStore();
const agents = useMapGetter('agents/getAgents');

const tickets = ref([]);
const status = ref('open');
const opened = ref(null);
const isLoading = ref(false);
const isSaving = ref(false);

const openedId = computed(() => Number(route.params.ticketId) || null);
const statusTabs = computed(() => [
  { status: 'open', label: t('CONVERSATION.OPERATOR_TICKETS.FILTER_OPEN') },
  { status: 'closed', label: t('CONVERSATION.OPERATOR_TICKETS.FILTER_CLOSED') },
]);

const formatDate = unix =>
  new Date(unix * 1000).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
  });

const fail = error =>
  useAlert(
    error.response?.data?.error || t('CONVERSATION.OPERATOR_TICKETS.FAILED')
  );

const loadTickets = async () => {
  isLoading.value = true;
  try {
    const { data } = await OperatorTicketsAPI.get({ status: status.value });
    tickets.value = data.payload;
  } catch (error) {
    tickets.value = [];
  } finally {
    isLoading.value = false;
  }
};

const loadOpened = async () => {
  if (!openedId.value) {
    opened.value = null;
    return;
  }
  try {
    const { data } = await OperatorTicketsAPI.show(openedId.value);
    opened.value = data;
  } catch (error) {
    opened.value = null;
    fail(error);
  }
};

const openTicket = ticket =>
  router.push({
    name: 'operator_tickets_index',
    params: { accountId: route.params.accountId, ticketId: ticket.id },
  });

// Whatever changed, the list and the open ticket show it at once; a ticket
// that left this filter (closed, reopened) leaves the list.
const run = async request => {
  isSaving.value = true;
  try {
    const { data } = await request();
    opened.value = data;
    const index = tickets.value.findIndex(ticket => ticket.id === data.id);
    if (index < 0) return;
    if (data.status === status.value) tickets.value.splice(index, 1, data);
    else tickets.value.splice(index, 1);
  } catch (error) {
    fail(error);
  } finally {
    isSaving.value = false;
  }
};

const id = () => opened.value.id;
const update = changes => run(() => OperatorTicketsAPI.update(id(), changes));
const addNote = content => run(() => OperatorTicketsAPI.addNote(id(), content));
const deleteNote = note =>
  run(() => OperatorTicketsAPI.deleteNote(id(), note.id));
const addParticipant = userId =>
  run(() => OperatorTicketsAPI.addParticipant(id(), userId));
const removeParticipant = userId =>
  run(() => OperatorTicketsAPI.removeParticipant(id(), userId));

const chatRoute = messageId => ({
  name: 'inbox_conversation',
  params: {
    accountId: route.params.accountId,
    conversation_id: opened.value.conversation_id,
  },
  query: messageId ? { messageId } : {},
});
const openChat = () => router.push(chatRoute());
const goToMessage = message => {
  if (opened.value.can_open_chat) router.push(chatRoute(message.id));
};

watch(status, loadTickets);
watch(openedId, loadOpened);

onMounted(() => {
  loadTickets();
  loadOpened();
  if (!agents.value.length) store.dispatch('agents/get');
});
</script>

<template>
  <div class="flex h-full w-full overflow-hidden bg-n-background">
    <aside
      class="flex w-80 shrink-0 flex-col gap-2 overflow-y-auto border-r border-n-weak p-3"
      :class="openedId ? 'hidden md:flex' : 'flex'"
    >
      <h1 class="px-1 text-lg font-semibold text-n-slate-12">
        {{ $t('CONVERSATION.OPERATOR_TICKETS.PANEL_TITLE') }}
      </h1>
      <div class="grid grid-cols-2 gap-1 rounded-lg bg-n-alpha-1 p-1">
        <button
          v-for="tab in statusTabs"
          :key="tab.status"
          type="button"
          class="rounded-md px-2 py-1 text-xs font-medium transition-colors"
          :class="
            status === tab.status
              ? 'bg-n-solid-3 text-n-slate-12 shadow-sm'
              : 'text-n-slate-11 hover:text-n-slate-12'
          "
          @click="status = tab.status"
        >
          {{ tab.label }}
        </button>
      </div>
      <button
        v-for="ticket in tickets"
        :key="ticket.id"
        type="button"
        class="flex flex-col gap-1 rounded-xl border px-3 py-2.5 text-left transition-colors"
        :class="
          ticket.id === openedId
            ? 'border-n-brand bg-n-solid-3'
            : 'border-n-weak bg-n-solid-2 hover:border-n-strong hover:bg-n-solid-3'
        "
        @click="openTicket(ticket)"
      >
        <span class="text-sm font-medium text-n-slate-12 line-clamp-2">
          {{ ticket.title }}
        </span>
        <span class="truncate text-xs text-n-slate-11">
          {{ [ticket.contact_name, ticket.inbox_name].filter(Boolean).join(' · ') }}
        </span>
        <span class="flex items-center gap-3 text-xs text-n-slate-10">
          <span class="flex items-center gap-1">
            <span class="i-lucide-message-square size-3.5" />
            {{ ticket.messages_count }}
          </span>
          <span class="flex items-center gap-1">
            <span class="i-lucide-clock size-3.5" />
            {{ formatDate(ticket.updated_at) }}
          </span>
          <span
            v-if="ticket.assignee_name"
            class="flex min-w-0 items-center gap-1"
          >
            <span class="i-lucide-user-round size-3.5 shrink-0" />
            <span class="truncate">{{ ticket.assignee_name }}</span>
          </span>
        </span>
      </button>
      <p
        v-if="!isLoading && !tickets.length"
        class="rounded-xl border border-dashed border-n-weak px-4 py-6 text-center text-xs text-n-slate-11"
      >
        {{ $t('CONVERSATION.OPERATOR_TICKETS.TAB_EMPTY') }}
      </p>
    </aside>

    <section class="min-w-0 flex-1 overflow-y-auto p-4">
      <div v-if="opened" class="mx-auto flex max-w-2xl flex-col gap-3">
        <div class="flex flex-wrap items-center justify-between gap-2">
          <NextButton
            xs
            ghost
            slate
            icon="i-lucide-chevron-left"
            class="md:hidden"
            :label="$t('CONVERSATION.OPERATOR_TICKETS.BACK')"
            @click="router.push({ name: 'operator_tickets_index' })"
          />
          <span class="truncate text-xs text-n-slate-11">
            {{ [opened.contact_name, opened.inbox_name].filter(Boolean).join(' · ') }}
          </span>
          <NextButton
            v-if="opened.can_open_chat"
            sm
            icon="i-lucide-message-circle"
            :label="$t('CONVERSATION.OPERATOR_TICKETS.OPEN_CHAT')"
            @click="openChat"
          />
          <span
            v-else
            class="flex items-center gap-1 text-xs text-n-slate-10"
          >
            <span class="i-lucide-lock size-3.5" />
            {{ $t('CONVERSATION.OPERATOR_TICKETS.NO_CHAT_ACCESS') }}
          </span>
        </div>
        <OperatorTicketDetail
          :ticket="opened"
          :is-saving="isSaving"
          :can-remove-messages="false"
          @update="update"
          @add-note="addNote"
          @delete-note="deleteNote"
          @go-to-message="goToMessage"
          @add-participant="addParticipant"
          @remove-participant="removeParticipant"
        />
      </div>
      <div
        v-else
        class="flex h-full flex-col items-center justify-center gap-2 text-n-slate-10"
      >
        <span class="i-lucide-ticket size-8" />
        <p class="text-sm">{{ $t('CONVERSATION.OPERATOR_TICKETS.PICK_TICKET') }}</p>
      </div>
    </section>
  </div>
</template>
